/**
 * @busmgr/session-expiry
 *
 * Shared session-expiry helpers for Business Manager web apps.
 * Gold standard originated in site_back_office — every auth failure path
 * should call forceSessionExpired() so the user sees a clear "sign in again"
 * message instead of a phantom empty state.
 *
 * Usage:
 *   import { createSessionExpiry, SESSION_REASON } from '@busmgr/session-expiry';
 *   const {
 *     forceSessionExpired,
 *     consumeSessionNotice,
 *     shouldBlockSilentSso,
 *     reasonFromAuthError,
 *     isSessionRedirectInProgress,
 *     isAuthFailure,
 *     attachVisibilityHeartbeat,
 *   } = createSessionExpiry({
 *     storageKeys: ['mm_token', 'mm_refreshToken', 'mm_user'],
 *     loginPath: '/',
 *   });
 */

'use strict';

const SESSION_REASON = Object.freeze({
  EXPIRED: 'session_expired',
  TENANT_MISMATCH: 'tenant_mismatch',
  AUTH_REQUIRED: 'auth_required',
});

const FLAG_KEY = 'busmgr_session_notice';
const OVERLAY_ID = 'busmgr-session-expired-overlay';

/**
 * @param {object} [options]
 * @param {string[]} [options.storageKeys] localStorage keys to clear on expiry
 * @param {string} [options.loginPath] pathname to redirect to (e.g. '/', '/login', '/admin')
 * @returns {object} session expiry helpers bound to the given keys/path
 */
function createSessionExpiry(options = {}) {
  const storageKeys = Array.isArray(options.storageKeys) && options.storageKeys.length
    ? options.storageKeys
    : ['token', 'refreshToken', 'user'];
  const loginPath = options.loginPath || '/';

  let _redirecting = false;

  function isSessionRedirectInProgress() {
    return _redirecting;
  }

  function isAuthFailure(err) {
    return err?.response?.status === 401 || err?.status === 401;
  }

  /**
   * Classify an API 401 body into a login-banner reason.
   */
  function reasonFromAuthError(err) {
    const msg = String(err?.response?.data?.message || err?.message || '').toLowerCase();
    if (msg.includes('does not match this tenant') || msg.includes('tenant')) {
      return SESSION_REASON.TENANT_MISMATCH;
    }
    if (msg.includes('no token') || msg.includes('authentication required')) {
      return SESSION_REASON.AUTH_REQUIRED;
    }
    return SESSION_REASON.EXPIRED;
  }

  function clearLocalSession() {
    try {
      for (const key of storageKeys) {
        localStorage.removeItem(key);
      }
    } catch (_) {
      /* ignore */
    }
  }

  function showBlockingOverlay(message) {
    if (typeof document === 'undefined') return;
    if (document.getElementById(OVERLAY_ID)) return;
    const el = document.createElement('div');
    el.id = OVERLAY_ID;
    el.setAttribute('role', 'alert');
    el.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:99999',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'background:rgba(15,23,42,0.72)',
      'padding:24px',
    ].join(';');
    el.innerHTML = `
    <div style="background:#fff;border-radius:12px;max-width:420px;width:100%;padding:20px 22px;
                box-shadow:0 20px 50px rgba(0,0,0,0.35);font-family:system-ui,sans-serif">
      <div style="font-weight:700;font-size:16px;color:#92400e;margin-bottom:8px">Sign in required</div>
      <div style="font-size:14px;color:#374151;line-height:1.45">${message}</div>
      <div style="font-size:12px;color:#6b7280;margin-top:12px">Redirecting to the login screen…</div>
    </div>
  `;
    document.body.appendChild(el);
  }

  /**
   * Clear credentials, show a blocking notice, and hard-redirect to login.
   * Safe to call multiple times — subsequent calls no-op.
   */
  function forceSessionExpired(reason = SESSION_REASON.EXPIRED, detailMessage) {
    if (_redirecting) return;
    _redirecting = true;

    const reasonKey = reason || SESSION_REASON.EXPIRED;
    const message =
      detailMessage ||
      (reasonKey === SESSION_REASON.TENANT_MISMATCH
        ? 'Your session does not match this workspace. Please sign in again.'
        : reasonKey === SESSION_REASON.AUTH_REQUIRED
          ? 'You need to sign in to continue.'
          : 'Your session has expired. Please sign in again.');

    clearLocalSession();

    try {
      sessionStorage.setItem(
        FLAG_KEY,
        JSON.stringify({ reason: reasonKey, message, at: Date.now() })
      );
    } catch (_) {
      /* ignore */
    }

    showBlockingOverlay(message);

    const url = new URL(window.location.href);
    url.pathname = loginPath;
    url.search = '';
    url.hash = '';
    url.searchParams.set('reason', reasonKey);

    // Allow the overlay to paint, then navigate.
    window.setTimeout(() => {
      window.location.replace(url.toString());
    }, 50);
  }

  /**
   * Read + clear the one-shot notice written before redirect.
   */
  function consumeSessionNotice() {
    try {
      const raw = sessionStorage.getItem(FLAG_KEY);
      if (raw) sessionStorage.removeItem(FLAG_KEY);
      const params = new URLSearchParams(window.location.search);
      const reason = params.get('reason');
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          reason: parsed.reason || reason || SESSION_REASON.EXPIRED,
          message: parsed.message || null,
        };
      }
      if (reason) {
        return { reason, message: null };
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  }

  /**
   * True when this page load was an intentional forced logout —
   * App bootstrap must NOT silently revive the session via SSO cookie.
   */
  function shouldBlockSilentSso() {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (
      reason === SESSION_REASON.EXPIRED ||
      reason === SESSION_REASON.TENANT_MISMATCH ||
      reason === SESSION_REASON.AUTH_REQUIRED
    ) {
      return true;
    }
    try {
      return Boolean(sessionStorage.getItem(FLAG_KEY));
    } catch (_) {
      return false;
    }
  }

  /**
   * Re-validate (or re-adopt) the session when the tab becomes visible.
   * Cookie-only SSO sessions have no localStorage JWT yet — missing token
   * must try adoptSession() instead of treating the user as logged out.
   *
   * @param {object} options
   * @param {string} [options.tokenKey] localStorage key for the Bearer JWT
   * @param {() => Promise<unknown>} [options.revalidate] GET /auth/me (or equivalent)
   * @param {() => Promise<unknown>} [options.adoptSession] checkSSOSession() — mint JWT from cookie
   * @returns {() => void} detach listener
   */
  function attachVisibilityHeartbeat(options = {}) {
    const tokenKey = options.tokenKey || storageKeys[0];
    const revalidate = options.revalidate;
    const adoptSession = options.adoptSession;

    async function onVisible() {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      if (_redirecting) return;

      let token = null;
      try {
        token = localStorage.getItem(tokenKey);
      } catch (_) {
        token = null;
      }

      try {
        if (token) {
          if (typeof revalidate === 'function') {
            await revalidate();
          }
          return;
        }
        if (typeof adoptSession === 'function') {
          const adopted = await adoptSession();
          if (adopted) return;
        }
        forceSessionExpired(SESSION_REASON.AUTH_REQUIRED);
      } catch (err) {
        if (isAuthFailure(err)) {
          forceSessionExpired(
            reasonFromAuthError(err),
            err?.response?.data?.message || err?.message
          );
        }
      }
    }

    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
      return function detach() {};
    }
    document.addEventListener('visibilitychange', onVisible);
    return function detach() {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }

  return {
    SESSION_REASON,
    forceSessionExpired,
    consumeSessionNotice,
    shouldBlockSilentSso,
    reasonFromAuthError,
    isSessionRedirectInProgress,
    isAuthFailure,
    clearLocalSession,
    attachVisibilityHeartbeat,
  };
}

module.exports = {
  __esModule: true,
  SESSION_REASON,
  createSessionExpiry,
};
